import { parse } from 'cookie'
import { GetServerSidePropsContext } from 'next'

import { getD2CAuthDomain, isB2BSelfServePreviewDomain } from '@src/config/b2b-auth-domain.config'
import {
  CONFIG_B2B_DOMAIN_SUFFIX,
  CONFIG_B2B_FEATURE_PREVIEW_DOMAIN_SUFFIX,
  CONFIG_B2B_SELF_SERVICE_DOMAIN,
} from '@src/config/env'
import {
  STORE_PROGRAM_ID_HEADER,
  B2B_TENANT_HEADER,
  B2B_FEATURE_PREVIEW_HEADER,
} from '@src/constants/api.headers.constants'
import { B2B_DENIED_DOMAINS } from '@src/constants/b2b-denied-domains'
import { STORE_PROGRAM_ID_KEY } from '@src/quince-site-common/src/services/cartCheckoutService/constants'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { B2B_TENANT_COOKIE } from '@src/utils/cross-domain-auth.helper'

import { getStorage } from '../enhancedStorage'

const isDevMode = (): boolean => process.env.NODE_ENV === 'development'

const getWindowHostname = (): string | undefined => (isBrowser() ? window.location.hostname : undefined)

export const getStoreProgramIdFromStorage = (): string | null => {
  if (!isBrowser()) return null

  const isB2BMode = isB2BSelfServeContext()

  if (isDevMode() && !isB2BMode) return null

  try {
    return getStorage().getItem(STORE_PROGRAM_ID_KEY)
  } catch (error) {
    console.error('Failed to read store program ID from storage:', error)
    return null
  }
}

export const getStorefrontIdProperty = (): { storefront_id: string } | Record<string, never> => {
  const id = getStoreProgramIdFromStorage()
  return id ? { storefront_id: id } : {}
}

export const getStoreProgramIdHeaders = (): Record<string, string> => {
  if (!isBrowser()) return {}
  const storeProgramId = getStoreProgramIdFromStorage()

  if (storeProgramId)
    return {
      [STORE_PROGRAM_ID_HEADER]: storeProgramId,
    }

  return {}
}

export const isB2BDeniedDomain = (domain: string): boolean => {
  try {
    const inputUrl = new URL(domain)

    return B2B_DENIED_DOMAINS.some(deniedDomain => {
      if (!deniedDomain) return false
      try {
        const deniedUrl = new URL(deniedDomain)
        return inputUrl.origin === deniedUrl.origin
      } catch {
        return inputUrl.origin.includes(deniedDomain)
      }
    })
  } catch (error) {
    console.error('Error checking B2B denied domain:', error)
    return false
  }
}

export const getB2BHeaders = (domain?: string): Record<string, string> => {
  if (!domain || isB2BDeniedDomain(domain)) return {}

  return { ...getStoreProgramIdHeaders() }
}

export const isB2BDomain = (host?: string): boolean => {
  const effectiveHost = host ?? getWindowHostname()
  return effectiveHost?.endsWith(CONFIG_B2B_DOMAIN_SUFFIX) ?? false
}

// Order tag from post-order / tracking APIs identifying a B2B self-serve order.
// eslint-disable-next-line i18next/no-literal-string
export const B2B_SELF_SERVICE_ORDER_TAG = 'B2B_SELF_SERVICE'

/** Support email for B2B self-serve orders (concierge). */
// eslint-disable-next-line i18next/no-literal-string
export const B2B_CONCIERGE_EMAIL = 'concierge@onequince.com'

export const isB2BSelfServiceOrder = (tags: string[] | undefined): boolean =>
  Array.isArray(tags) && tags.includes(B2B_SELF_SERVICE_ORDER_TAG)

export const isB2BSelfServeDomain = (host?: string): boolean => {
  const effectiveHost = host ?? getWindowHostname()
  if (!effectiveHost) return false
  // Strip port if present (server-side host header may include it)
  const hostname = effectiveHost.split(':')[0]
  return hostname === CONFIG_B2B_SELF_SERVICE_DOMAIN || isB2BSelfServePreviewDomain(hostname)
}

/** True when running on a B2B self-serve surface (deployed domain, preview, or localhost with B2B route). */
export const isB2BSelfServeContext = (): boolean => {
  if (!isBrowser()) return false
  const hostname = window.location.hostname
  if (isB2BSelfServeDomain()) return true
  if (hostname === 'localhost') {
    // On localhost, only match if there's a B2B signal (route prefix or tenant cookie)
    /* eslint-disable @quince/no-direct-cookie, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- cookie@0.4.2 lacks TS types */
    const cookies: Record<string, string> = parse(document.cookie)
    /* eslint-enable @quince/no-direct-cookie, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
    const hasTenantCookie = !!cookies[B2B_TENANT_COOKIE]
    return window.location.pathname.startsWith('/b2b/') || hasTenantCookie
  }
  return false
}

export const getB2BTenant = (ctx: GetServerSidePropsContext): string | undefined => {
  const host = ctx.req.headers.host
  const headers = ctx.req.headers
  // eslint-disable-next-line i18next/no-literal-string

  //this is to run b2b in local environment with tenant header
  if (process.env.NODE_ENV === 'development' && host?.includes('localhost')) {
    /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- cookie@0.4.2 lacks TS types */
    const cookieTenant = ctx.req.headers.cookie
      ? (parse(ctx.req.headers.cookie)[B2B_TENANT_COOKIE] as string | undefined)
      : undefined
    /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const headerTenant = ctx.req.headers[B2B_TENANT_HEADER] as string | undefined
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const tenant = headerTenant ?? (ctx.query.tenant as string | undefined) ?? cookieTenant
    return tenant
  }

  const featurePreviewHeaderValue = (headers[B2B_FEATURE_PREVIEW_HEADER] as string | undefined) ?? ''
  const isB2BFeaturePreview = featurePreviewHeaderValue.includes(CONFIG_B2B_FEATURE_PREVIEW_DOMAIN_SUFFIX)

  if (isB2BFeaturePreview) return featurePreviewHeaderValue.split('-')[0]

  if (isB2BSelfServeDomain(host)) return CONFIG_B2B_SELF_SERVICE_DOMAIN.split('.')[0]

  if (!isB2BDomain(host)) return undefined

  // Extract tenant name by removing the domain suffix
  const tenant = host?.slice(0, -CONFIG_B2B_DOMAIN_SUFFIX.length)

  // Validate tenant name (should not be empty and should not contain dots)
  if (!tenant || tenant.includes('.')) {
    return undefined
  }

  return tenant
}

export const getQuinceStorefrontUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  if (isDevMode()) return normalizedPath

  // Use getD2CAuthDomain() for preview-aware domain resolution (handles preview branch domains)
  const d2cDomain = getD2CAuthDomain()
  const protocol = isBrowser() ? window.location.protocol : 'https:'
  return `${protocol}//${d2cDomain}${normalizedPath}`
}
